import React, { useEffect, useState } from 'react';
import { Table, Spinner } from 'reactstrap';
import moment from 'moment';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { fetchEmployeeFacilities } from '../../../store/apis/employeeService';
import '../index.scss';
import ModalWrapper from '../../Common/ModalWrapper';
import ModalTable from '../../Common/ModalTable/ModalTable';

function FacilityInfo({ data, isLoading }) {
  const employeeData = Array.isArray(data) ? data : [];

  const [claimedMap, setClaimedMap] = useState({});
  const [loadingClaims, setLoadingClaims] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    dataConfig: [],
    data: [],
  });

  const { mutateAsync: fetchFacilities } = useMutation({
    mutationFn: fetchEmployeeFacilities,
    onError: err => toast.error(err?.message || 'Failed to fetch facilities'),
  });

  const handleClaimClick = async fac => {
    try {
      const payload = {
        depName: fac.Dependent,
        type: fac.Facility,
        fromDate: moment(fac['Valid From']).format('YYYY-MM-DD'),
        toDate: moment(fac['Valid To']).format('YYYY-MM-DD'),
      };

      const response = await fetchFacilities(payload);
      const rows = Array.isArray(response) ? response : [];

      const validFrom = moment(fac['Valid From']);
      const validTo = moment(fac['Valid To']);
      const filteredRows = rows.filter(r =>
        moment(r.Date).isBetween(validFrom, validTo, 'day', '[]'),
      );

      // Add index as a string key, keep all row keys strings
      const indexedRows = filteredRows.map((row, idx) => ({
        index: String(idx + 1),
        Reference: row.Reference || '',
        Amount: row.Amount
          ? Number(row.Amount).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : '0.00',
        Date: row.Date ? moment(row.Date).format('L') : '',
      }));

      setModalConfig({
        title: `Claimed History - ${fac.Dependent} (${fac.Facility})`,
        dataConfig: [
          {
            key: 'Reference',
            label: 'Claim Number',
          },
          {
            key: 'Amount',
            label: 'Claimed Amount',
          },
          {
            key: 'Date',
            label: 'Date',
          },
        ],
        data: indexedRows,
      });

      setIsModalOpen(true);
    } catch (err) {
      toast.error('Failed to fetch claim details');
    }
  };

  useEffect(() => {
    if (!employeeData.length) return;

    const fetchClaims = async () => {
      setLoadingClaims(true);

      try {
        const resultsArray = await Promise.all(
          employeeData
            .filter(fac => fac.Facility === 'School Fees')
            .map(async fac => {
              try {
                const payload = {
                  depName: fac.Dependent,
                  type: fac.Facility,
                  fromDate: moment(fac['Valid From']).format('YYYY-MM-DD'),
                  toDate: moment(fac['Valid To']).format('YYYY-MM-DD'),
                };

                const response = await fetchFacilities(payload);
                const rows = Array.isArray(response) ? response : [];

                const validFrom = moment(fac['Valid From']);
                const validTo = moment(fac['Valid To']);
                const filteredRows = rows.filter(r => {
                  const claimDate = moment(r.Date);
                  return claimDate.isBetween(validFrom, validTo, 'day', '[]');
                });

                const totalClaimed = filteredRows.reduce(
                  (sum, r) => sum + Number(r.Amount || 0),
                  0,
                );

                return { key: fac.Dependent, totalClaimed };
              } catch {
                return { key: fac.Dependent, totalClaimed: 0 };
              }
            }),
        );

        const results = resultsArray.reduce((acc, item) => {
          acc[item.key] = item.totalClaimed;
          return acc;
        }, {});

        setClaimedMap(results);
      } finally {
        setLoadingClaims(false);
      }
    };

    fetchClaims();
  }, [employeeData, fetchFacilities]);

  const renderTableBody = () => {
    if (isLoading || loadingClaims) {
      return (
        <tr>
          <td colSpan="9">
            <div className="d-flex justify-content-center align-items-center gap-1">
              <Spinner color="dark" size="sm" />
              <span>Loading...</span>
            </div>
          </td>
        </tr>
      );
    }

    const validEmployeeData = employeeData.filter(
      fac => fac.Facility && fac.Facility !== '-',
    );

    return employeeData.length ? (
      validEmployeeData.map(fac => {
        const claimed = claimedMap[fac.Dependent] ?? 0;
        const limit = Number(fac?.Limit ?? 0);
        const balance = limit - claimed;

        return (
          <tr key={`${fac.Facility}_${fac.Dependent}`}>
            <td>{fac.Facility}</td>
            <td>{fac.Category}</td>
            <td>{fac.Dependent || '-'}</td>
            <td>{limit.toLocaleString()}</td>
            {fac.Facility === 'School Fees' ? (
              <td
                style={{
                  cursor: 'pointer',
                  color: 'blue',
                  textDecoration: 'underline',
                }}
                onClick={() => handleClaimClick(fac)}
              >
                {claimed.toLocaleString()}
              </td>
            ) : (
              <td>{claimed.toLocaleString()}</td>
            )}
            <td>{balance.toLocaleString()}</td>
            <td>{moment(fac['Valid From']).format('L')}</td>
            <td>{moment(fac['Valid To']).format('L')}</td>
            <td>{fac.Comments || '-'}</td>
          </tr>
        );
      })
    ) : (
      <tr>
        <td colSpan="9" className="text-center">
          No facilities found.
        </td>
      </tr>
    );
  };

  return (
    <div className="p-0">
      <Table
        hover
        className="custom-table align-middle text-nowrap"
        id="facility-profile-table"
      >
        <thead>
          <tr>
            <th>Facility</th>
            <th>Category</th>
            <th>Dependent</th>
            <th>Limit</th>
            <th>Claimed</th>
            <th>Balance</th>
            <th>Valid From</th>
            <th>Valid To</th>
            <th>Comments</th>
          </tr>
        </thead>
        <tbody>{renderTableBody()}</tbody>
      </Table>

      {/* ✅ New ModalWrapper instead of FacilityModal */}
      <ModalWrapper
        isModalOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        title={modalConfig.title}
        onButtonClick={() => setIsModalOpen(false)}
        buttonText="Close"
        size="xl"
      >
        <ModalTable
          data={modalConfig.data}
          dataConfig={modalConfig.dataConfig}
          index
        />
      </ModalWrapper>
    </div>
  );
}

export default FacilityInfo;
